// Keep in sync with server/src/utils/whatnot-taxonomy.js (compared in whatnot-mirrors.test.js).

/**
 * Whatnot's categories and their attributes, read from the snapshot of Whatnot's own product taxonomy
 * (data/whatnot-config.json, the ids Seller Hub sends).
 *
 * On Whatnot an item's details — its condition, size, brand, colour — are answers to attributes of its
 * category, and each category asks its own: a sneaker's condition is asked in different words than a
 * dress's, and a dress has a size where a handbag has none. The questions Voolist pre-fills from a
 * listing are found here by what they ask about, never by a category name.
 */

import whatnotConfig from '../data/whatnot-config.json';

const CATEGORIES_BY_ID = new Map(whatnotConfig.categories.map((category) => [category.id, category]));
const ATTRIBUTES_BY_ID = new Map(whatnotConfig.attributes.map((attribute) => [attribute.id, attribute]));

// The attributes that hold the size of something worn, and the size scale each one asks in. Whatnot's
// other "Size" attributes measure other things (a record's diameter, a plush toy's height, a bottle of
// perfume) and are never pre-filled from a listing's size.
const SIZE_SCALES = Object.freeze({
  1: 'footwear', // Sneakers
  1025: 'footwear', // Golf shoes
  39982: 'footwear', // Women's shoes
  74: 'clothing', // Clothing in the fashion categories
  142133: 'clothing', // Golf apparel
  87: 'kids', // Baby and kids' clothes and shoes
  104676: 'tailoring', // Men's formalwear
});

const lastPartOf = (key) => String(key || '').split('.').pop();

// What an attribute asks about, from the last part of its key ("fashion.thrift.condition").
const ASKS_ABOUT = Object.freeze({
  condition: (attribute) => lastPartOf(attribute.key) === 'condition',
  brand: (attribute) => lastPartOf(attribute.key) === 'brand',
  color: (attribute) => lastPartOf(attribute.key) === 'color',
  colorway: (attribute) => lastPartOf(attribute.key) === 'colorway',
  size: (attribute) => attribute.id in SIZE_SCALES,
});

/** A Whatnot category by id, or null. */
export const categoryById = (categoryId) => CATEGORIES_BY_ID.get(Number(categoryId)) || null;

/** A Whatnot attribute by id, or null. */
export const attributeById = (attributeId) => ATTRIBUTES_BY_ID.get(Number(attributeId)) || null;

/** Whether a listing can be filed in this category (Whatnot lists only in its leaf categories). */
export const isListableCategory = (categoryId) => Boolean(categoryById(categoryId)?.listable);

/** The category and every category above it, nearest first. */
export const categoryWithAncestors = (categoryId) => {
  const ids = [];
  let category = categoryById(categoryId);
  while (category && !ids.includes(category.id)) {
    ids.push(category.id);
    category = category.parentId == null ? null : categoryById(category.parentId);
  }
  return ids;
};

/** The category's place in Whatnot's tree, top first: ["Men's Fashion", "Men's Modern"]. */
export const categoryPathLabels = (categoryId) => categoryWithAncestors(categoryId).reverse()
  .map((id) => categoryById(id).label);

/** Every category a listing can be filed in, with its path and the details it requires, in Whatnot's order. */
export const listableCategories = () => whatnotConfig.categories
  .filter((category) => category.listable)
  .map((category) => ({
    id: category.id,
    label: category.label,
    path: categoryPathLabels(category.id),
    required: category.required || [],
  }));

/** The attributes this category asks, in Whatnot's order. */
export const attributesOfCategory = (categoryId) => (categoryById(categoryId)?.attributes || [])
  .map(attributeById)
  .filter(Boolean);

/**
 * The category's attribute that asks about this: 'condition', 'size', 'brand', 'color' or 'colorway'.
 * Null when the category does not ask it.
 */
export const categoryAttributeAbout = (categoryId, topic) => {
  const asks = ASKS_ABOUT[topic];
  if (!asks) return null;
  return attributesOfCategory(categoryId).find(asks) || null;
};

/** The scale a size attribute asks in ('footwear', 'clothing', 'kids', 'tailoring'), or null. */
export const sizeScaleOf = (attribute) => (attribute ? SIZE_SCALES[attribute.id] || null : null);

/**
 * What a listing in this category answers "Hazardous Materials" when the seller has not: the category's own
 * default (fragrances and nails contain hazardous materials, electronics lithium batteries), else none —
 * the answer Whatnot's own form gives.
 */
export const defaultHazmatOf = (categoryId) => categoryById(categoryId)?.hazmat || 'NOT_HAZMAT';

/**
 * One of the attribute's own values that reads the same as `value` ignoring case, spelled as the
 * attribute spells it; null when the attribute offers no such value.
 */
export const offeredValue = (attribute, value) => {
  if (!attribute || value == null || value === '') return null;
  const wanted = String(value).trim().toLowerCase();
  return (attribute.values || []).find((own) => own.toLowerCase() === wanted) || null;
};
